import {
    Article,
    AttachMoney,
    Block,
    Cancel,
    CheckCircle,
    Delete,
    Favorite,
    HelpOutline,
    List as ListIcon,
    Message,
    MoneyOff,
    OpenInNew,
    Person,
    Save,
    Subscriptions,
    Videocam,
    VolumeOff,
    Warning,
    WavingHand,
} from '@mui/icons-material';
import {
    alpha,
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Link as MuiLink,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from 'react-scroll';
import { NameObject, OnlyFansUserProfile } from '../../../hooks/useInsightsUserProfile';
import { CompareFanSpendingResult, SimpleFanSpendingGaugeResult } from '../../../hooks/useInsightsUserSpendingsCompare';

interface UserProfileDisplayProps {
    sextforceUser?: OnlyFansUserProfile | null;
    loading?: boolean;
    insightsUserProfileRefetch: () => void;
    insightsSaveUserRealName: (realName: string) => Promise<OnlyFansUserProfile | null>;
    isInsightsUserProfileSaving: boolean;
    comparisonResult?: CompareFanSpendingResult | SimpleFanSpendingGaugeResult | null;
}

// Format currency
const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) {
        return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

// Format date
const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

// Status Badge Component
const StatusBadge: React.FC<{
    label: string;
    active: boolean;
    icon: React.ReactElement;
    color: 'error' | 'warning' | 'success' | 'info';
}> = ({ label, active, icon, color }) => {
    const theme = useTheme();

    if (!active) return null;

    return <Chip icon={icon} label={label} color={color} size="small" sx={{ px: 0.5 }} />;
};

const UserProfile: React.FC<UserProfileDisplayProps> = ({
    sextforceUser,
    loading = false,
    insightsUserProfileRefetch,
    insightsSaveUserRealName,
    isInsightsUserProfileSaving,
    comparisonResult,
}) => {
    const { user } = sextforceUser || {};
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    const [realName, setRealName] = useState<string>(sextforceUser?.realName || '');

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="body1" color="text.secondary">
                    Loading...
                </Typography>
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="body1" color="text.secondary">
                    No user data available
                </Typography>
            </Box>
        );
    }

    const hasSubscribedOnData = user?.subscribedOnData && Object.keys(user?.subscribedOnData).length > 0;

    const handleNameClick = (nameObj: NameObject | undefined) => {
        if (!nameObj) return;

        let fullName = '';

        if (nameObj.firstName) fullName += nameObj.firstName;

        if (nameObj.lastName) fullName += (fullName ? ' ' : '') + nameObj.lastName;

        setRealName(fullName);
    };

    const handleSaveRealName = () => {
        if (realName.trim() === (sextforceUser?.realName || '').trim()) {
            return;
        }

        insightsSaveUserRealName(realName.trim())
            .then(updatedProfile => {
                if (updatedProfile) {
                    enqueueSnackbar('Real name updated successfully', { variant: 'success' });

                    insightsUserProfileRefetch();
                } else {
                    enqueueSnackbar('Failed to update real name', { variant: 'error' });
                }
            })
            .catch(() => {
                enqueueSnackbar('Failed to update real name', { variant: 'error' });
            });
    };

    return (
        <Stack spacing={2}>
            {/* Header Section */}
            <Stack direction="row" spacing={2} alignItems="flex-start">
                <Avatar
                    src={user?.avatarThumbs?.c50 || undefined}
                    sx={{
                        width: 80,
                        height: 80,
                        // border: `3px solid ${theme.palette.primary.main}`,
                    }}
                >
                    <Person sx={{ fontSize: 40 }} />
                </Avatar>

                <Stack spacing={2} sx={{ flex: 1 }}>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {user.displayName || user.name}
                        </Typography>
                        <Stack direction="row" spacing={0} alignItems="center">
                            <Typography variant="h6" color="text.secondary">
                                @{user.username}
                            </Typography>
                            <IconButton
                                component={RouterLink}
                                to={`https://onlyfans.com/${user.username}`}
                                target="_blank"
                                size="small"
                                aria-label="Open in OnlyFans"
                                title="Open in OnlyFans"
                            >
                                <OpenInNew />
                            </IconButton>
                        </Stack>
                        {user.name !== user.displayName && (
                            <Typography variant="body2" color="text.secondary">
                                Name on OnlyFans: {user.name}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </Stack>

            {/* Real Name */}
            <Stack direction="column" spacing={1}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <TextField
                        label={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <div>Real Name</div>
                                <Tooltip title="This name will be used in Priority Messaging messaging services.">
                                    <HelpOutline fontSize="small" color="inherit" />
                                </Tooltip>
                            </Stack>
                        }
                        variant="outlined"
                        fullWidth
                        InputProps={{
                            startAdornment: <Person sx={{ color: theme.palette.action.active, mr: 1 }} />,
                        }}
                        disabled={isInsightsUserProfileSaving}
                        value={realName}
                        onChange={e => setRealName(e.target.value)}
                    />
                    <IconButton
                        size="small"
                        disabled={realName.trim() === (sextforceUser?.realName || '').trim() || isInsightsUserProfileSaving}
                        onClick={handleSaveRealName}
                        title="Save Real Name"
                        aria-label="Save Real Name"
                    >
                        <Save />
                    </IconButton>
                </Stack>
                {sextforceUser && sextforceUser.possibleRealName && sextforceUser.possibleRealName.firstName && (
                    <Typography variant="body2" color="text.secondary">
                        Possible Real Name:{' '}
                        <MuiLink
                            onClick={() => handleNameClick(sextforceUser.possibleRealName)}
                            color={theme.palette.primary.main}
                            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                            {sextforceUser.possibleRealName.firstName} {sextforceUser.possibleRealName.lastName || ''}
                        </MuiLink>
                    </Typography>
                )}
            </Stack>

            {/* Status Badges */}
            {(user.isBlocked || user.isRestricted || user.isMuted || user.isSuspended) && (
                <Stack direction="row" spacing={0} flexWrap="wrap" gap={1}>
                    <StatusBadge label="Blocked" active={user.isBlocked} icon={<Block />} color="error" />
                    <StatusBadge label="Restricted" active={user.isRestricted} icon={<Warning />} color="warning" />
                    <StatusBadge label="Muted" active={user.isMuted} icon={<VolumeOff />} color="info" />
                    <StatusBadge label="Suspended" active={user.isSuspended || false} icon={<Warning />} color="error" />
                    <StatusBadge label="Deleted" active={user.isDeleted || false} icon={<Delete />} color="error" />
                </Stack>
            )}

            {/* Total Spending */}
            {hasSubscribedOnData && (
                <Card
                    sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        border: `2px solid ${theme.palette.primary.main}`,
                        borderRadius: theme.shape.borderRadius,
                    }}
                >
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <AttachMoney sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Total Net Revenue
                                </Typography>
                                <Typography variant="h4" fontWeight={700} color="primary">
                                    {formatCurrency(user.subscribedOnData.totalSumm)}
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* Status Badges */}
            {comparisonResult &&
                comparisonResult instanceof Object &&
                'summary' in comparisonResult &&
                comparisonResult.summary &&
                comparisonResult.summary.behavioralMetrics &&
                comparisonResult.summary.behavioralMetrics.length > 0 && (
                    <Stack direction="row" spacing={0} flexWrap="wrap" gap={1}>
                        {comparisonResult.summary.behavioralMetrics.find(
                            m => m.flag === 'known_freeloader' && (m.severity === 'medium' || m.severity === 'high'),
                        ) && (
                            <Link to="metric-known_freeloader" smooth={true} duration={500} style={{ cursor: 'pointer' }}>
                                <StatusBadge label="Known Freeloader" active={true} icon={<MoneyOff />} color="warning" />
                            </Link>
                        )}
                        {comparisonResult.summary.behavioralMetrics.find(
                            m => m.flag === 'often_blocked' && (m.severity === 'medium' || m.severity === 'high'),
                        ) && (
                            <Link to="metric-often_blocked" smooth={true} duration={500} style={{ cursor: 'pointer' }}>
                                <StatusBadge label="Often Blocked" active={true} icon={<WavingHand />} color="error" />
                            </Link>
                        )}
                    </Stack>
                )}

            {/* About Section */}
            {user.about && (
                <Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        About
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {user.about}
                    </Typography>
                </Box>
            )}

            {/* Relationship Status */}
            <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    Relationship Status
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Card
                            sx={{
                                backgroundColor: user.subscribedBy
                                    ? alpha(theme.palette.success.main, 0.1)
                                    : alpha(theme.palette.grey[500], 0.1),
                                border: `1px solid ${user.subscribedBy ? theme.palette.success.main : theme.palette.grey[400]}`,
                                borderRadius: theme.shape.borderRadius,
                            }}
                        >
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    {user.subscribedBy ? (
                                        <CheckCircle sx={{ color: theme.palette.success.main }} />
                                    ) : (
                                        <Cancel sx={{ color: theme.palette.grey[500] }} />
                                    )}
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            You're Following
                                        </Typography>
                                        <Typography variant="h6" fontWeight={600}>
                                            {user.subscribedBy ? 'Yes' : 'No'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card
                            sx={{
                                backgroundColor: user.subscribedOn
                                    ? alpha(theme.palette.info.main, 0.1)
                                    : alpha(theme.palette.grey[500], 0.1),
                                border: `1px solid ${user.subscribedOn ? theme.palette.info.main : theme.palette.grey[400]}`,
                            }}
                        >
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    {user.subscribedOn ? (
                                        <CheckCircle sx={{ color: theme.palette.info.main }} />
                                    ) : (
                                        <Cancel sx={{ color: theme.palette.grey[500] }} />
                                    )}
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Following You
                                        </Typography>
                                        <Typography variant="h6" fontWeight={600}>
                                            {user.subscribedOn ? 'Yes' : 'No'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* List States */}
            {user.listsStates && user.listsStates.length > 0 && (
                <Box>
                    <Stack direction="row" alignItems="center" justifyContent={'space-between'} spacing={1} mb={2}>
                        <Typography variant="h6" fontWeight={600}>
                            Lists
                        </Typography>
                        <Tooltip title="Add to/Remove from Lists in OnlyFans">
                            <span>
                                <IconButton>
                                    <ListIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                        {user.listsStates
                            .filter(list => list.hasUser)
                            .map((list, index) => (
                                <Chip
                                    key={index}
                                    label={list.name}
                                    color={list.hasUser ? 'primary' : 'default'}
                                    variant={list.hasUser ? 'filled' : 'outlined'}
                                />
                            ))}
                        {user.listsStates.filter(list => list.hasUser).length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Not in any lists.
                            </Typography>
                        )}
                    </Stack>
                </Box>
            )}

            {/* Subscription Details */}
            {hasSubscribedOnData && (
                <>
                    {/* Revenue Breakdown */}
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            Revenue Breakdown
                        </Typography>
                        <List>
                            {[
                                {
                                    name: 'Messages',
                                    value: user.subscribedOnData.messagesSumm || 0,
                                    color: theme.palette.primary.main,
                                    icon: <Message sx={{ color: theme.palette.primary.main }} />,
                                },
                                {
                                    name: 'Tips',
                                    value: user.subscribedOnData.tipsSumm || 0,
                                    color: theme.palette.secondary.main,
                                    icon: <Favorite sx={{ color: theme.palette.secondary.main }} />,
                                },
                                {
                                    name: 'Subscriptions',
                                    value: user.subscribedOnData.subscribesSumm || 0,
                                    color: theme.palette.success.main,
                                    icon: <Subscriptions sx={{ color: theme.palette.success.main }} />,
                                },
                                {
                                    name: 'Posts',
                                    value: user.subscribedOnData.postsSumm || 0,
                                    color: theme.palette.warning.main,
                                    icon: <Article sx={{ color: theme.palette.warning.main }} />,
                                },
                                {
                                    name: 'Streams',
                                    value: user.subscribedOnData.streamsSumm || 0,
                                    color: theme.palette.info.main,
                                    icon: <Videocam sx={{ color: theme.palette.info.main }} />,
                                },
                            ].map((item, index) => {
                                const percentage =
                                    user.subscribedOnData.totalSumm > 0 ? (item.value / user.subscribedOnData.totalSumm) * 100 : 0;

                                return (
                                    <React.Fragment key={index}>
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" fontWeight={600}>
                                                        {item.name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography variant="body2" color="text.secondary">
                                                        {percentage.toFixed(1)}% of total revenue
                                                    </Typography>
                                                }
                                            />
                                            <Typography variant="h6" fontWeight={700}>
                                                {formatCurrency(item.value)}
                                            </Typography>
                                        </ListItem>
                                        <Box sx={{ mb: 1, px: 0 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={percentage}
                                                sx={{
                                                    height: 10,
                                                    borderRadius: 5,
                                                    backgroundColor: alpha(item.color, 0.25),
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: item.color,
                                                        borderRadius: 5,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    </Box>

                    {/* Subscription Info */}
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            Current Subscription
                        </Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                                <List dense>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemText
                                            primary="Subscribe Price"
                                            secondary={formatCurrency(user.subscribedOnData.subscribePrice)}
                                        />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemText primary="Current Price" secondary={formatCurrency(user.subscribedOnData.price)} />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemText
                                            primary="Regular Price"
                                            secondary={formatCurrency(user.subscribedOnData.regularPrice)}
                                        />
                                    </ListItem>
                                    {user.subscribedOnData.discountPercent > 0 && (
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary="Discount" secondary={`${user.subscribedOnData.discountPercent}% off`} />
                                        </ListItem>
                                    )}
                                </List>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <List dense>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemText primary="Subscribed On" secondary={formatDate(user.subscribedOnData.subscribeAt)} />
                                    </ListItem>
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemText primary="Expires On" secondary={formatDate(user.subscribedOnData.expiredAt)} />
                                    </ListItem>
                                    {user.subscribedOnData.renewedAt && (
                                        <ListItem sx={{ pl: 0 }}>
                                            <ListItemText primary="Last Renewed" secondary={formatDate(user.subscribedOnData.renewedAt)} />
                                        </ListItem>
                                    )}
                                    <ListItem sx={{ pl: 0 }}>
                                        <ListItemText primary="Duration" secondary={user.subscribedOnData.duration || 'N/A'} />
                                    </ListItem>
                                </List>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Subscription History */}
                    {user.subscribedOnData.subscribes && user.subscribedOnData.subscribes.length > 0 && (
                        <Box>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Subscription History
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Duration</TableCell>
                                            <TableCell align="right">Price</TableCell>
                                            <TableCell align="right">Regular Price</TableCell>
                                            <TableCell align="right">Discount</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {user.subscribedOnData.subscribes.map((sub, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Typography variant="body2" fontSize="0.75rem">
                                                        {formatDate(sub.date)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{sub.type}</TableCell>
                                                <TableCell>{sub.duration} days</TableCell>
                                                <TableCell align="right">{formatCurrency(sub.price)}</TableCell>
                                                <TableCell align="right">{formatCurrency(sub.regularPrice)}</TableCell>
                                                <TableCell align="right">
                                                    {sub.discount > 0 ? (
                                                        <Chip
                                                            label={`${sub.discount}%`}
                                                            size="small"
                                                            color="success"
                                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={sub.isCurrent ? 'Current' : 'Expired'}
                                                        size="small"
                                                        color={sub.isCurrent ? 'primary' : 'default'}
                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </>
            )}
        </Stack>
    );
};

export default UserProfile;
